import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosClient from '../api/axiosClient';

const Navbar = ({ onToggleSidebar, isSidebarOpen = false }) => {
  const { user, logout, switchFirm, switchFid } = useAuth();
  const navigate = useNavigate();
  const [firms, setFirms] = useState([]);
  const [years, setYears] = useState([]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axiosClient.get('/auth/config');
        if (res.success) {
          setFirms(res.data.firms || []);
          setYears(res.data.years || []);
        }
      } catch (e) {
        console.error('Error fetching navbar config:', e);
      }
    };
    fetchConfig();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const onFirmChange = (e) => {
    const fId = parseInt(e.target.value, 10);
    const selected = firms.find((f) => f.Id === fId);
    if (selected) {
      switchFirm(fId, selected.firmName);
      window.location.reload();
    }
  };

  const onYearChange = (e) => {
    const fid = parseInt(e.target.value, 10);
    const selected = years.find((y) => y.Fid === fid);
    if (selected) {
      const sY = new Date(selected.StartYear).getFullYear();
      const eY = new Date(selected.EndYear).getFullYear();
      switchFid(fid, `${sY}-${eY}`);
      window.location.reload();
    }
  };

  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('#userDropdownContainer')) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <nav className="navbar navbar-expand-lg navbar-dark app-navbar sticky-top px-2 px-sm-3">
      <div className="container-fluid">
        {/* Mobile Hamburger Menu Toggle Button */}
        <button
          className="btn btn-sm btn-outline-light border-0 d-lg-none me-2 p-1 d-flex align-items-center justify-content-center"
          type="button"
          onClick={onToggleSidebar}
          aria-label="Toggle Menu"
          title="Toggle Navigation Menu"
          style={{ width: '36px', height: '36px', borderRadius: '8px' }}
        >
          <i className={`bi ${isSidebarOpen ? 'bi-x-lg text-warning' : 'bi-list text-white'} fs-4`}></i>
        </button>

        <Link className="navbar-brand d-flex align-items-center gap-2 py-1 text-decoration-none me-2" to="/dashboard" title="Dashboard">
          <div className="rounded-circle bg-info bg-opacity-25 p-2 d-flex align-items-center justify-content-center border border-info border-opacity-50 flex-shrink-0" style={{ width: '38px', height: '38px' }}>
            <i className="bi bi-shop text-info fs-5"></i>
          </div>
          <div className="d-flex flex-column text-start overflow-hidden">
            <span className="fw-bold tracking-wide text-uppercase text-white text-truncate" style={{ fontSize: 'clamp(0.95rem, 1.8vw, 1.2rem)', letterSpacing: '0.5px', lineHeight: '1.2', maxWidth: '280px' }}>
              {user?.firmName || 'FISH SHOP MUMBAI'}
            </span>
            <span className="text-info opacity-75 fw-semibold d-none d-sm-inline" style={{ fontSize: '0.68rem', letterSpacing: '0.8px' }}>
              FISH SHOP MUMBAI ONLINE
            </span>
          </div>
        </Link>

        <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
          {/* Active Firm Selector / Badge */}
          {user?.role === 'admin' ? (
            <div className="d-flex align-items-center gap-1 bg-dark bg-opacity-50 px-2 py-1 rounded border border-secondary">
              <i className="bi bi-building text-warning"></i>
              <select
                className="form-select form-select-sm bg-transparent text-white border-0 py-0"
                style={{ cursor: 'pointer', maxWidth: '240px' }}
                value={user?.firmId || 1}
                onChange={onFirmChange}
                title="Switch Firm (Admin Mode)"
              >
                <option value="ALL" className="text-dark fw-bold">
                  ★ All Firms (Consolidated View)
                </option>
                {firms.map((f) => (
                  <option key={f.Id} value={f.Id} className="text-dark">
                    {f.firmName}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-1 bg-dark bg-opacity-50 px-2 py-1 rounded border border-secondary text-white small">
              <i className="bi bi-building text-warning"></i>
              <span className="fw-semibold text-truncate" style={{ maxWidth: '220px' }}>
                {user?.firmName || 'Assigned Firm'}
              </span>
            </div>
          )}

          {/* Financial Year Selector */}
          <div className="d-flex align-items-center gap-1 bg-dark bg-opacity-50 px-2 py-1 rounded border border-secondary">
            <i className="bi bi-calendar3 text-info"></i>
            <select
              className="form-select form-select-sm bg-transparent text-white border-0 py-0"
              style={{ cursor: 'pointer' }}
              value={user?.fid || 4}
              onChange={onYearChange}
            >
              {years.map((y) => {
                const sY = new Date(y.StartYear).getFullYear();
                const eY = new Date(y.EndYear).getFullYear();
                return (
                  <option key={y.Fid} value={y.Fid} className="text-dark">
                    FY {sY}-{eY}
                  </option>
                );
              })}
            </select>
          </div>

          {/* User Profile & Actions Dropdown */}
          <div className="dropdown" id="userDropdownContainer">
            <button
              className={`btn btn-sm btn-outline-light d-flex align-items-center gap-2 ${userMenuOpen ? 'active show' : ''}`}
              type="button"
              id="userMenuBtn"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              aria-expanded={userMenuOpen}
            >
              <i className="bi bi-person-circle fs-5 text-info"></i>
              <span className="fw-semibold d-none d-md-inline">{user?.username || 'User'}</span>
              <span className={`badge ${user?.role === 'admin' ? 'bg-warning text-dark' : 'bg-info text-dark'} text-uppercase ms-1`} style={{ fontSize: '0.65rem' }}>
                {user?.role || 'user'}
              </span>
              <i className="bi bi-chevron-down small text-muted"></i>
            </button>

            <ul
              className={`dropdown-menu dropdown-menu-end shadow-lg border-0 ${userMenuOpen ? 'show' : ''}`}
              aria-labelledby="userMenuBtn"
              style={{
                minWidth: '260px',
                borderRadius: '12px',
                marginTop: '8px',
                display: userMenuOpen ? 'block' : 'none'
              }}
            >
              {/* Profile Header */}
              <li className="px-3 py-2 bg-light border-bottom rounded-top">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold shadow-sm"
                    style={{ width: '40px', height: '40px', fontSize: '1.1rem' }}
                  >
                    {(user?.username || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="overflow-hidden">
                    <div className="fw-bold text-dark text-truncate">{user?.username}</div>
                    <div className="small text-muted text-truncate" title={user?.firmName}>
                      <i className="bi bi-building me-1"></i>{user?.firmName || 'Assigned Branch'}
                    </div>
                  </div>
                </div>
              </li>

              {/* Navigation Links */}
              <li>
                <Link
                  className="dropdown-item d-flex align-items-center gap-2 py-2"
                  to="/settings/password"
                  onClick={() => setUserMenuOpen(false)}
                >
                  <i className="bi bi-key-fill text-secondary"></i>
                  <span>Change Password</span>
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item d-flex align-items-center gap-2 py-2"
                  to="/settings/firm"
                  onClick={() => setUserMenuOpen(false)}
                >
                  <i className="bi bi-gear-fill text-secondary"></i>
                  <span>Firm Settings</span>
                </Link>
              </li>

              <li><hr className="dropdown-divider my-1" /></li>

              {/* Logout Option in Profile */}
              <li>
                <button
                  className="dropdown-item text-danger fw-bold d-flex align-items-center gap-2 py-2 bg-danger bg-opacity-10 rounded-bottom"
                  onClick={() => {
                    setUserMenuOpen(false);
                    handleLogout();
                  }}
                  id="profileLogoutBtn"
                >
                  <i className="bi bi-box-arrow-right fs-5 text-danger"></i>
                  <span>Logout from Session</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Quick Direct Logout Button for Fast Access */}
          <button
            className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 text-nowrap"
            onClick={handleLogout}
            title="Logout"
            id="navbarQuickLogoutBtn"
          >
            <i className="bi bi-power"></i>
            <span className="d-none d-lg-inline">Logout</span>
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
